import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { PageHeader } from '../../components/Shared';
import {
  getCustomerWorkOrder,
  listWorkOrderRams,
  fetchWorkOrderPdfUrl,
} from '../../services/api';
import { usePdfPreview } from '../../hooks/usePdfPreview';
import { PdfPreviewDialog } from '../../components/PdfPreviewDialog';
import {
  ArrowLeft, MapPin, Wrench, Calendar, CheckCircle2, ClipboardList,
  FileText, Download, ImageIcon,
} from 'lucide-react';
import { Button } from '../../components/ui/button';

const IMAGE_EXTS = ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.heic', '.heif'];
const isImage = (name = '') => IMAGE_EXTS.some((ext) => name.toLowerCase().endsWith(ext));

// Uploaded files live behind /api/uploads/{storedAs} — the same endpoint
// the admin uses. Attachments have this url baked in as `storedAs`
// (relative path), so we prepend the app's backend origin.
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const attachmentUrl = (att) => {
  if (!att) return '';
  if (att.url) return att.url.startsWith('http') ? att.url : `${BACKEND}${att.url}`;
  if (att.storedAs) return `${BACKEND}/api/uploads/${att.storedAs}`;
  return '';
};

/**
 * Read-only, customer-facing "what did we do on this job" page.
 * Reached from the WO reference on invoice PDF line items.
 *
 * Sections:
 *   1. Facts card — title / site / dates / status / assets covered.
 *   2. Photos gallery — image attachments rendered as thumbnails.
 *   3. Files list — non-image attachments with download.
 *   4. RAMS documents — each generated version opens as PDF in a new tab.
 *   5. Closure summary — engineer's on-site notes.
 */
export default function CustomerWorkOrder() {
  const { id } = useParams();
  const [wo, setWo] = useState(null);
  const [rams, setRams] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  // Inline PDF preview modal — same pattern as CustomerInvoices &
  // AdminInvoiceDetail. Avoids iOS Safari popup blockers that kick in
  // when window.open() is called after an `await` (i.e. outside the
  // original user-gesture tick), which was the root cause of the
  // "Could not open work order PDF" toast on mobile.
  const pdf = usePdfPreview();

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([
      getCustomerWorkOrder(id),
      // RAMS list is optional — a WO without RAMS just shows an empty
      // section. Never let the RAMS call block the main content.
      listWorkOrderRams(id).catch(() => []),
    ])
      .then(([w, r]) => {
        if (!alive) return;
        setWo(w);
        setRams(Array.isArray(r) ? r : []);
      })
      .catch((e) => {
        if (!alive) return;
        setError(e?.response?.data?.detail || 'Could not load this work order');
      })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [id]);

  // Fetch and preview the full Work Order PDF — same endpoint the
  // admin uses. Any RAMS attached to the WO are automatically appended
  // as an appendix by the backend, so this single button gives the
  // customer the exact bundle the admin sees. Renders inline via
  // PdfPreviewDialog so the user gesture stays intact on iOS Safari.
  const openWorkOrderPdf = () => pdf.preview(() => fetchWorkOrderPdfUrl(id));

  if (loading) return <div className="p-6 text-sm text-slate-500">Loading…</div>;
  if (error) {
    return (
      <div className="p-6 space-y-3" data-testid="customer-wo-error">
        <div className="text-sm text-red-600 font-medium">{error}</div>
        <Link to="/customer/invoices">
          <Button variant="outline" size="sm"><ArrowLeft className="h-4 w-4 mr-1.5" /> Back to invoices</Button>
        </Link>
      </div>
    );
  }
  if (!wo) return null;

  const dates = [wo.startByDate, wo.finishByDate || wo.completedAt].filter(Boolean);
  const dateLine = dates.length === 2 ? `${dates[0]} → ${dates[1]}` : (dates[0] || '');
  const attachments = wo.attachments || [];
  const photos = attachments.filter((a) => isImage(a.filename || a.storedAs || ''));
  const files = attachments.filter((a) => !isImage(a.filename || a.storedAs || ''));

  return (
    <div className="space-y-4" data-testid={`customer-wo-${wo.id}`}>
      <PageHeader
        title={wo.title || `Work order ${wo.id}`}
        subtitle={<span className="font-mono text-xs">{wo.id}</span>}
        breadcrumb={<Link to="/customer/invoices" className="text-slate-500 hover:text-slate-800 text-sm inline-flex items-center gap-1"><ArrowLeft className="h-3.5 w-3.5" /> All invoices</Link>}
        actions={
          <Button
            onClick={openWorkOrderPdf}
            disabled={pdf.busy}
            data-testid="customer-wo-pdf-download"
            className="inline-flex items-center gap-1.5"
          >
            <FileText className="h-4 w-4" />
            {pdf.busy ? 'Opening…' : (
              rams.length > 0
                ? `Work Order PDF (incl. RAMS)`
                : 'Work Order PDF'
            )}
          </Button>
        }
      />

      <div className="bg-white border border-slate-200 rounded-md p-5 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
          {wo.site && (
            <div className="flex items-start gap-2 text-slate-700">
              <MapPin className="h-4 w-4 text-slate-500 mt-0.5 shrink-0" />
              <div>
                <div className="font-medium">{wo.site.name}</div>
                {wo.site.address && <div className="text-xs text-slate-500">{wo.site.address}</div>}
              </div>
            </div>
          )}
          {dateLine && (
            <div className="flex items-center gap-2 text-slate-700">
              <Calendar className="h-4 w-4 text-slate-500" />
              <span>{dateLine}</span>
            </div>
          )}
          {wo.status && (
            <div className="flex items-center gap-2 text-slate-700">
              <ClipboardList className="h-4 w-4 text-slate-500" />
              <span className="capitalize">{wo.status.replace(/_/g, ' ')}</span>
            </div>
          )}
          {wo.completedAt && (
            <div className="flex items-center gap-2 text-emerald-700">
              <CheckCircle2 className="h-4 w-4" />
              <span>Completed {wo.completedAt}</span>
            </div>
          )}
        </div>

        {(wo.assets && wo.assets.length > 0) && (
          <div>
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">
              Assets covered ({wo.assets.length})
            </div>
            <ul className="flex flex-wrap gap-1.5">
              {wo.assets.map((a) => (
                <li key={a.id} className="inline-flex items-center gap-1 bg-slate-100 border border-slate-200 rounded-full px-2.5 py-0.5 text-xs">
                  <Wrench className="h-3 w-3 text-slate-500" />
                  {a.name}
                </li>
              ))}
            </ul>
          </div>
        )}

        {wo.description && (
          <div>
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">Description</div>
            <div className="text-sm text-slate-800 whitespace-pre-wrap">{wo.description}</div>
          </div>
        )}
      </div>

      {/* Photos — image attachments in a responsive grid */}
      {photos.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-md p-5 space-y-3" data-testid="customer-wo-photos">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
            <ImageIcon className="h-3.5 w-3.5" />
            Photos ({photos.length})
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {photos.map((p, i) => {
              const src = attachmentUrl(p);
              return (
                <a
                  key={p.storedAs || i}
                  href={src}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block aspect-square bg-slate-100 rounded-md overflow-hidden border border-slate-200 hover:border-slate-400 transition"
                  data-testid={`customer-wo-photo-${i}`}
                  title={p.filename || 'Photo'}
                >
                  <img
                    src={src}
                    alt={p.filename || `Photo ${i + 1}`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </a>
              );
            })}
          </div>
        </div>
      )}

      {/* Other files — non-image attachments (PDFs, docs, etc.) */}
      {files.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-md p-5 space-y-3" data-testid="customer-wo-files">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
            Files ({files.length})
          </div>
          <ul className="divide-y divide-slate-100">
            {files.map((f, i) => (
              <li key={f.storedAs || i} className="flex items-center justify-between py-2 gap-3">
                <div className="flex items-center gap-2 min-w-0">
                  <FileText className="h-4 w-4 text-slate-400 shrink-0" />
                  <span className="text-sm text-slate-800 truncate">{f.filename || 'file'}</span>
                </div>
                <a
                  href={attachmentUrl(f)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-emerald-700 hover:text-emerald-900"
                >
                  <Download className="h-3.5 w-3.5" /> Download
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* RAMS documents are now bundled into the single "Work Order PDF"
          download in the header — matching what the admin sees — so we
          no longer render a separate RAMS section here. */}

      {wo.closureSummary && (
        <div className="bg-white border border-slate-200 rounded-md p-5">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">
            Summary of work carried out
          </div>
          <div className="text-sm text-slate-800 whitespace-pre-wrap">{wo.closureSummary}</div>
          {wo.closureSummaryBy && (
            <div className="text-[11px] text-slate-500 mt-2">
              — {wo.closureSummaryBy}{wo.closureSummaryAt && `, ${wo.closureSummaryAt.slice(0, 10)}`}
            </div>
          )}
        </div>
      )}

      <PdfPreviewDialog
        url={pdf.url}
        filename={pdf.filename}
        onClose={pdf.close}
        title="Work order PDF"
      />
    </div>
  );
}
